import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import { setReflowGroupsAction, updateMediaElementAction } from 'editor/src/store/design/slice';
import { MediaText, ReflowGroup, ReflowRow, StructureIndex } from 'editor/src/store/design/types';
import {
  getReflowBounds,
  getReflowMemberLeft,
  isReflowMember,
  ReflowMember,
} from 'editor/src/store/design/util/reflowMember';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import { RootState } from 'editor/src/store/index';

import { extractTextProperties } from 'editor/src/component/EditorArea/Spread/Page/MediaElement/Text/updateTextElementWithoutRender';

import getMediaElementByUuid from '../selector/getMediaElementByUuid';
import { getStructureIndexByElementUuid } from '../selector/getStructureIndexByElementUuid';

import { getPathVisualWidth, getReflowMemberPackWidth } from './computeReflowState';
import { getConditionSiblingElementUuids } from './conditionReflowSync';
import updateHiddenElementsOperation from './updateHiddenElementsOperation';

function isMediaText(element: ReflowMember): element is MediaText {
  return element.type === 'text';
}

let counter = 0;
const nextId = (prefix: string): string => {
  counter += 1;
  return `${prefix}-${counter}-${Math.floor(Math.random() * 100000).toString(36)}`;
};

export const cloneGroups = (groups: ReflowGroup[]): ReflowGroup[] =>
  groups.map((g) => ({
    id: g.id,
    name: g.name,
    rows: g.rows.map((r) => ({
      id: r.id,
      optional: r.optional,
      elementUuids: [...r.elementUuids],
      shiftY: r.shiftY,
      horizontalReflow: r.horizontalReflow,
      elementShiftsX: r.elementShiftsX ? { ...r.elementShiftsX } : undefined,
      elementGaps: r.elementGaps ? { ...r.elementGaps } : undefined,
      elementWidthOffsets: r.elementWidthOffsets
        ? Object.fromEntries(Object.entries(r.elementWidthOffsets).map(([uuid, offset]) => [uuid, { ...offset }]))
        : undefined,
      dividerUuids: r.dividerUuids ? [...r.dividerUuids] : undefined,
      horizontalAlign: r.horizontalAlign,
      alignWidth: r.alignWidth,
    })),
  }));

const getGroups = (state: RootState, spreadIndex: number): ReflowGroup[] =>
  state.design.designData?.spreads[spreadIndex]?.reflowGroups ?? [];

/**
 * Remove an element from every row across all groups — both its membership
 * (`elementUuids`) AND any divider mark (`dividerUuids`). Mutates the passed
 * (already-cloned) groups. Single source of truth so an element can never
 * linger as a divider on a row it has left (on cross-row move or unlink).
 */
const detachElementFromRows = (groups: ReflowGroup[], elementUuid: number): void => {
  groups.forEach((g) =>
    g.rows.forEach((r) => {
      r.elementUuids = r.elementUuids.filter((u) => u !== elementUuid);
      if (r.elementWidthOffsets) {
        delete r.elementWidthOffsets[String(elementUuid)];
      }
      if (r.dividerUuids) {
        const next = r.dividerUuids.filter((u) => u !== elementUuid);
        r.dividerUuids = next.length > 0 ? next : undefined;
      }
    }),
  );
};

/**
 * Authored span width (mm) of a row: max(authoredX + width) - min(authoredX)
 * over ALL members, as if all were visible. `widthOf` supplies each member's
 * pack width. Returns undefined when no member resolves.
 */
const computeRowAlignWidth = (
  members: ReflowMember[],
  row: ReflowRow,
  widthOf: (el: ReflowMember) => number,
): number | undefined => {
  if (members.length === 0) {
    return undefined;
  }
  const shiftsX = row.elementShiftsX ?? {};
  const spans = members.map((el) => {
    const x = getReflowMemberLeft(el) - (shiftsX[String(el.uuid)] ?? 0);
    return { left: x, right: x + widthOf(el) };
  });
  return Math.max(...spans.map((s) => s.right)) - Math.min(...spans.map((s) => s.left));
};

const commit = (dispatch: ThunkDispatch, spreadIndex: number, next: ReflowGroup[]): void => {
  batch(() => {
    dispatch(setReflowGroupsAction({ spreadIndex, reflowGroups: next }));
    // Any structural mutation of reflow groups can change which rows are
    // collapsed → which elements should be hidden. Mirror the Conditions
    // pattern (e.g. removeConditionOptionOperation) and recompute in the
    // same batch so the render sees a consistent state.
    dispatch(updateHiddenElementsOperation());
  });
};

export const addReflowGroupOperation =
  (spreadIndex: number, defaultName: string) => (dispatch: ThunkDispatch, getState: () => RootState) => {
    const current = getGroups(getState(), spreadIndex);
    const groups = cloneGroups(current);
    groups.push({
      id: nextId('rg'),
      name: `${defaultName} ${groups.length + 1}`,
      rows: [],
    });
    commit(dispatch, spreadIndex, groups);
  };

export const renameReflowGroupOperation =
  (spreadIndex: number, groupId: string, name: string) => (dispatch: ThunkDispatch, getState: () => RootState) => {
    const groups = cloneGroups(getGroups(getState(), spreadIndex));
    const target = groups.find((g) => g.id === groupId);
    if (!target) {
      return;
    }
    target.name = name;
    commit(dispatch, spreadIndex, groups);
  };

export const deleteReflowGroupOperation =
  (spreadIndex: number, groupId: string) => (dispatch: ThunkDispatch, getState: () => RootState) => {
    const groups = cloneGroups(getGroups(getState(), spreadIndex)).filter((g) => g.id !== groupId);
    commit(dispatch, spreadIndex, groups);
  };

export const addReflowRowOperation =
  (spreadIndex: number, groupId: string, elementUuids: number[] = [], optional = true) =>
  (dispatch: ThunkDispatch, getState: () => RootState) => {
    const groups = cloneGroups(getGroups(getState(), spreadIndex));
    const target = groups.find((g) => g.id === groupId);
    if (!target) {
      return;
    }
    const row: ReflowRow = {
      id: nextId('rr'),
      optional,
      elementUuids: [...elementUuids],
    };
    target.rows.push(row);
    commit(dispatch, spreadIndex, groups);
  };

export const removeReflowRowOperation =
  (spreadIndex: number, groupId: string, rowId: string) => (dispatch: ThunkDispatch, getState: () => RootState) => {
    const groups = cloneGroups(getGroups(getState(), spreadIndex));
    const target = groups.find((g) => g.id === groupId);
    if (!target) {
      return;
    }
    target.rows = target.rows.filter((r) => r.id !== rowId);
    commit(dispatch, spreadIndex, groups);
  };

export const setReflowRowOptionalOperation =
  (spreadIndex: number, groupId: string, rowId: string, optional: boolean) =>
  (dispatch: ThunkDispatch, getState: () => RootState) => {
    const groups = cloneGroups(getGroups(getState(), spreadIndex));
    const target = groups.find((g) => g.id === groupId);
    const row = target?.rows.find((r) => r.id === rowId);
    if (!target || !row) {
      return;
    }
    row.optional = optional;
    commit(dispatch, spreadIndex, groups);
  };

export const addElementToReflowRowOperation =
  (spreadIndex: number, groupId: string, rowId: string, elementUuid: number) =>
  (dispatch: ThunkDispatch, getState: () => RootState) => {
    const groups = cloneGroups(getGroups(getState(), spreadIndex));
    const target = groups.find((g) => g.id === groupId);
    const row = target?.rows.find((r) => r.id === rowId);
    if (!target || !row) {
      return;
    }
    if (row.elementUuids.includes(elementUuid)) {
      return;
    }
    // Also strip the uuid from any other row in any group so an element belongs
    // to exactly one row at a time — same invariant as Conditions. Detaches the
    // divider mark too, so it never trails the element onto its new row.
    detachElementFromRows(groups, elementUuid);
    row.elementUuids.push(elementUuid);
    commit(dispatch, spreadIndex, groups);
  };

export const setReflowRowHorizontalOperation =
  (spreadIndex: number, groupId: string, rowId: string, horizontalReflow: boolean) =>
  (dispatch: ThunkDispatch, getState: () => RootState) => {
    const state = getState();
    const groups = cloneGroups(getGroups(state, spreadIndex));
    const target = groups.find((g) => g.id === groupId);
    const row = target?.rows.find((r) => r.id === rowId);
    if (!target || !row) {
      return;
    }
    row.horizontalReflow = horizontalReflow || undefined;
    row.elementWidthOffsets = undefined;

    batch(() => {
      if (horizontalReflow) {
        const members = row.elementUuids
          .map((uuid) => {
            const el = getMediaElementByUuid(state, uuid);
            const addr = getStructureIndexByElementUuid(state.design.designData, uuid);
            return el && isReflowMember(el) && addr ? { el, addr } : undefined;
          })
          .filter((e): e is { el: ReflowMember; addr: StructureIndex } => !!e);

        if (members.length > 0) {
          // Compute the reflow content width for each element ONCE (path bbox, or
          // a re-measured content width for normal fonts, or the bounding-box width
          // for a non-text member) and reuse it for BOTH the gap capture and the
          // width update. This guarantees the gap is measured against the SAME
          // width that computeReflowState's packing advances by
          // (getHorizontalReflowWidth / the bbox width) — otherwise normal-font
          // boxes, whose authored width is wider than their content, would capture
          // the gap against the wide box and pack against the narrow content,
          // collapsing the spacing.
          const reflowWidthByUuid = new Map<number, number>();
          const heightByUuid = new Map<number, number>();
          members.forEach(({ el }) => {
            if (!isMediaText(el)) {
              // Non-text members don't get flexibleWidth/width normalisation (that's
              // text-only), just their bbox width for gap capture below.
              const bounds = getReflowBounds(el);
              reflowWidthByUuid.set(el.uuid, bounds.right - bounds.left);
              return;
            }
            if (el.path) {
              reflowWidthByUuid.set(el.uuid, getPathVisualWidth(el) ?? el.width);
            } else {
              const merged = { ...el, extra: { ...el.extra, flexibleWidth: true } };
              const props = extractTextProperties(merged, undefined);
              reflowWidthByUuid.set(el.uuid, props.width);
              heightByUuid.set(el.uuid, props.height);
            }
          });

          // Capture the designer-authored gap (mm) before each element, in visual
          // order. Use AUTHORED left edge (strip any residual shift) so the gap is
          // taken from the imported layout, not a packed one.
          const shiftsX = row.elementShiftsX ?? {};
          const authoredX = (el: ReflowMember) => getReflowMemberLeft(el) - (shiftsX[String(el.uuid)] ?? 0);
          const sortedByX = [...members].sort((a, b) => authoredX(a.el) - authoredX(b.el));
          const gaps: Record<string, number> = {};
          for (let i = 1; i < sortedByX.length; i += 1) {
            const prev = sortedByX[i - 1].el;
            const curr = sortedByX[i].el;
            const prevWidth = reflowWidthByUuid.get(prev.uuid) ?? 0;
            gaps[String(curr.uuid)] = Math.max(0, authoredX(curr) - (authoredX(prev) + prevWidth));
          }
          row.elementGaps = gaps;
          row.alignWidth = computeRowAlignWidth(
            members.map((m) => m.el),
            row,
            (el) => reflowWidthByUuid.get(el.uuid) ?? 0,
          );

          members.forEach(({ el, addr }) => {
            if (!isMediaText(el)) {
              // Only text needs flexibleWidth/width normalisation; a non-text
              // member's own geometry is untouched — it just joins the gap capture
              // above and computeReflowState's packing.
              return;
            }
            const elementUpdate: Partial<MediaText> = {
              extra: { ...el.extra, flexibleWidth: true },
              width: reflowWidthByUuid.get(el.uuid),
            };
            const height = heightByUuid.get(el.uuid);
            if (height !== undefined) {
              elementUpdate.height = height;
            }
            dispatch(updateMediaElementAction({ elementAddress: addr, elementUpdate }));
          });
        }
      }

      dispatch(setReflowGroupsAction({ spreadIndex, reflowGroups: groups }));
      dispatch(updateHiddenElementsOperation());
    });
  };

export const removeElementFromReflowRowOperation =
  (spreadIndex: number, groupId: string, rowId: string, elementUuid: number) =>
  (dispatch: ThunkDispatch, getState: () => RootState) => {
    const groups = cloneGroups(getGroups(getState(), spreadIndex));
    const target = groups.find((g) => g.id === groupId);
    const row = target?.rows.find((r) => r.id === rowId);
    if (!target || !row) {
      return;
    }
    // Element belongs to exactly one row, so detaching across all rows is
    // equivalent to removing it from this one, and also clears the divider mark.
    detachElementFromRows(groups, elementUuid);
    commit(dispatch, spreadIndex, groups);
  };

/** Mark / unmark an element in a reflow row as a divider (separator). A divider
 *  is shown only when both its nearest non-divider neighbours are present and
 *  non-empty; computeReflowState hides it otherwise. */
export const setReflowRowDividerOperation =
  (spreadIndex: number, groupId: string, rowId: string, elementUuid: number, isDivider: boolean) =>
  (dispatch: ThunkDispatch, getState: () => RootState) => {
    const groups = cloneGroups(getGroups(getState(), spreadIndex));
    const row = groups.find((g) => g.id === groupId)?.rows.find((r) => r.id === rowId);
    if (!row || !row.elementUuids.includes(elementUuid)) {
      return;
    }
    const current = new Set(row.dividerUuids ?? []);
    if (isDivider) {
      current.add(elementUuid);
    } else {
      current.delete(elementUuid);
    }
    row.dividerUuids = current.size > 0 ? [...current] : undefined;
    commit(dispatch, spreadIndex, groups);
  };

/** Set the horizontal alignment of a horizontal-reflow row ('left' clears it)
 *  and capture the row's authored span width so the visible content can be
 *  centred / right-aligned within it. */
export const setReflowRowAlignOperation =
  (spreadIndex: number, groupId: string, rowId: string, align: 'left' | 'center' | 'right') =>
  (dispatch: ThunkDispatch, getState: () => RootState) => {
    const state = getState();
    const groups = cloneGroups(getGroups(state, spreadIndex));
    const row = groups.find((g) => g.id === groupId)?.rows.find((r) => r.id === rowId);
    if (!row) {
      return;
    }
    if (align === 'left') {
      row.horizontalAlign = undefined;
      row.alignWidth = undefined;
    } else {
      row.horizontalAlign = align;
      const members = row.elementUuids
        .map((uuid) => getMediaElementByUuid(state, uuid))
        .filter((el): el is ReflowMember => !!el && isReflowMember(el));
      row.alignWidth = computeRowAlignWidth(members, row, (el) => getReflowMemberPackWidth(el, row));
    }
    commit(dispatch, spreadIndex, groups);
  };

/**
 * After an element is linked to a condition option, if any sibling option's
 * element already belongs to a reflow row, add this element to that same row so
 * all options of the condition reflow together. No-op when the element isn't a
 * condition option or none of its siblings are in a reflow row.
 */
export const addConditionElementToSiblingReflowRowOperation =
  (spreadIndex: number, elementUuid: number) => (dispatch: ThunkDispatch, getState: () => RootState) => {
    const state = getState();
    const spread = state.design.designData?.spreads[spreadIndex];
    const conditionGroup = spread?.conditionGroup;
    if (!spread || !conditionGroup || !spread.reflowGroups?.length) {
      return;
    }

    const siblings = getConditionSiblingElementUuids(conditionGroup, elementUuid).filter((u) => u !== elementUuid);
    if (siblings.length === 0) {
      return;
    }

    let match: { groupId: string; rowId: string; alreadyMember: boolean } | undefined;
    spread.reflowGroups.forEach((group) =>
      group.rows.forEach((row) => {
        if (!match && row.elementUuids.some((u) => siblings.includes(u))) {
          match = {
            groupId: group.id,
            rowId: row.id,
            alreadyMember: row.elementUuids.includes(elementUuid),
          };
        }
      }),
    );

    if (match && !match.alreadyMember) {
      dispatch(addElementToReflowRowOperation(spreadIndex, match.groupId, match.rowId, elementUuid));
    }
  };

/**
 * Strips an element from every reflow row (across all groups). Used when an
 * element is unlinked from a condition option or its option is removed, so the
 * reflow rows stay in sync with the condition's current options.
 */
export const removeElementFromReflowGroupsOperation =
  (spreadIndex: number, elementUuid: number) => (dispatch: ThunkDispatch, getState: () => RootState) => {
    const current = getGroups(getState(), spreadIndex);
    const isMember = current.some((g) => g.rows.some((r) => r.elementUuids.includes(elementUuid)));
    if (!isMember) {
      return;
    }
    const groups = cloneGroups(current);
    detachElementFromRows(groups, elementUuid);
    commit(dispatch, spreadIndex, groups);
  };
